import { ResetGameCommand } from "ags/commands/reset-game-fatal-error-command";
import { AgsSpinResponseSchema, AgsSpinSchema } from "ags/integration/schema/ags-spin-response-schema";
import { gameState } from "appworks/model/game-state";
import { Gameplay } from "appworks/model/gameplay/gameplay";
import { Record } from "appworks/model/gameplay/records/record";
import { builderManager } from "appworks/server/builders/builder-manager";
import { RecordBuilder } from "appworks/server/builders/record-builder";
import { Services } from "appworks/services/services";
import { TransactionService } from "appworks/services/transaction/transaction-service";
import { gameiomModel } from "gameiom/model/gameiom-model";
import { FreespinRecord } from "slotworks/model/gameplay/records/freespin-record";
import { SpinRecord } from "slotworks/model/gameplay/records/spin-record";
import { FreespinRequestPayload } from "slotworks/model/gameplay/requests/freespin-request-payload";
import { SpinRequestPayload } from "slotworks/model/gameplay/requests/spin-request-payload";
import { slotDefinition } from "slotworks/model/slot-definition";
import { slotModel } from "slotworks/model/slot-model";
import { agsRecordUtils } from "../ags-record-utils";
import { AgsRecordBuilder } from "./abstract-ags-record-builder";

export class SpinRecordBuilder extends AgsRecordBuilder implements RecordBuilder {
    public id: string = "AgsSpinRecordBuilder";

    public filter(request: any, response: any): boolean {
        return response && (request instanceof SpinRequestPayload || request instanceof FreespinRequestPayload);
    }

    public build(gameplay: Gameplay, request: any, response: any): Record[] {
        const rootData = this.getRootAgsRecord(response);

        if(rootData.config == null) {
            throw(`Config is null in the spin response. Session may have been closed.`);
        }

        this.setCreditValues(rootData.config);

        gameState.autoDeductStake = !slotModel.read().bet.freeBet;

        const platform = rootData.platform;
        if (platform.responsibleGamingEvent || rootData.error) {
            const multiplier = gameiomModel.isSocial ? 1 : 100;
            const realBalance = (platform?.balance ?? 0) * multiplier;
            const freeBalance = (platform?.balanceDetails?.bonus ?? 0) * multiplier;

            gameplay.balance = Math.round(realBalance);
            gameplay.freeBalance = Math.round(freeBalance);
            Services.get(TransactionService).setBalance(gameState.getCurrentGame().balance);

            // Handle the responsible gaming case. balancesUpdate is called twice in a row
            // to match the expected flow for the wrapper with latest documentation.
            if (platform.responsibleGamingEvent) {
                gameWrapper.balancesUpdate(
                    gameplay.balance,
                    gameplay.freeBalance
                );
    
                gameWrapper.balancesUpdate(
                    gameplay.balance,
                    gameplay.freeBalance
                );
            }

            // SG requirement - fatal error
            ResetGameCommand();
            return [];
        }

        super.build(gameplay, request, response);

        const rootSpin = rootData.publicState.spin;
        const spin = this.getLatestAgsSpin(rootData.publicState) as AgsSpinSchema;

        const spinRecord: SpinRecord = this.buildRecord(spin, request instanceof FreespinRequestPayload, rootSpin, rootData);
        spinRecord.wager = rootData.publicState.betAmount * rootData.publicState.denom;

        return [spinRecord];
    }

    public buildRecord(spin: AgsSpinSchema, freespin: boolean, rootSpin: AgsSpinSchema, rootData: AgsSpinResponseSchema): SpinRecord {
        const record = this.createNewRecord(spin, freespin, rootSpin);

        if (spin.reels) {
            const grid = agsRecordUtils.convertreelsToSlotworksGrid(spin.reels);
            record.grid = grid;
        }

        record.reelset = slotDefinition.reelsets.get("base");
        if (freespin && slotDefinition.reelsets.has("freespins")) {
            record.reelset = slotDefinition.reelsets.get("freespins");
        }

        record.cashWon = spin.sumWins;

        record.results = [];
        for (const agsWin of [...spin.wins, ...spin.events]) {
            record.results = record.results.concat(builderManager.buildResults(record, agsWin, rootData));
        }

        // Build results which aren't inside wins or events...
        record.results = record.results.concat(builderManager.buildResults(record, spin, rootData));
        record.results = record.results.concat(builderManager.buildResults(record, rootData));

        if (!spin.complete && !freespin) {
            record.hasChildren = true;
        }

        if (!(record instanceof FreespinRecord) || rootSpin.complete) {
            record.lastChild = true;
        }

        return record;
    }

    protected createNewRecord(spin: AgsSpinSchema, freespin: boolean, rootSpin: AgsSpinSchema) {
        return freespin ? new FreespinRecord() : new SpinRecord();
    }
}
